import { BlockIOPSMap, ROOT_BLOCK_ID, blockConfigs } from "../../models/block";
import { CanvasPositionState } from "../../models/canvas";
import { ReactFlowHandleWithNodeInfo } from "../../models/connectors";
import { GameState, getIOPSSummary } from "../../models/game";
import {
    Edge,
    Node,
} from "reactflow";

export const selectBlocks = (state: GameState) => state.blocks;
export const selectConnectors = (state: GameState) => state.connectors;
export const selectGameStatus = (state: GameState) => ({
    isStarted: state.isStarted,
    isRunning: state.isRunning,
    isOver: state.isOver,
    accountBalance: state.accountBalance,
    timeElapsedInSec: state.timeElapsedInSec,
    level: state.level,
});
export const selectBlockIOPSSummary = (
    iopsState: BlockIOPSMap,
    blockID: string
) => getIOPSSummary(iopsState, blockID);
export const selectCanvasPositionState = (state: GameState): CanvasPositionState => {
    return {
        blocks: state.blocks,
        connectors: state.connectors,
        draggingBlock: state.draggingBlock,
        connectionStartBlock: state.connectionStartBlock,
        selectedBlock: state.selectedBlock,
    };
};
export const selectAccountBalance = (state: GameState) => state.accountBalance;

export const selectRootPercentageSuccess = (state: GameState) => {
    const rootIOPS = state.blockIOPS[ROOT_BLOCK_ID];
    return rootIOPS.percentageSuccess;
}

//A selector that maps all blocks to react-flow Nodes and exports it
export const selectBlocksAsNodes = (state: GameState): Node[] => {
    return Object.values(state.blocks).map((block) => {
        return {
            id: block.id,
            position: { x: block.left, y: block.top },
            data: {
                label: blockConfigs[block.blockType].displayName,
            },
        };
    });
}

//A selector that exports _nodes from the store
export const selectNodes = (state: GameState): Node[] => {
    return state.nodes;
}

//A selector that exports _edges from the store
export const selectEdges = (state: GameState): Edge[] => {
    return state.edges;
}

//select the selected node id from the store
export const selectedNode = (state: GameState) => {
    const selectedNodeArr = state.nodes.filter((node) => node.selected)
    return selectedNodeArr.length > 0 ? selectedNodeArr[0].id : null;
}

//a selector that returns a node from _nodes by id
export const selectNodeByID = (state: GameState, id: string) => {
    const result = state.nodes.filter((node) => node.id === id)
    return result.length > 0 ? result[0] : null;
}

//select the dragging node id from the store
export const selectDragHandleNode = (state: GameState): ReactFlowHandleWithNodeInfo | null => {
    const dragHandleEvent = state.connectionStartNode;
    if (!dragHandleEvent) return null;
    const result = state.nodes.filter((node) => node.id === dragHandleEvent.nodeID)
    if (result.length === 0) return null;
    return { ...dragHandleEvent, blockType: result[0].data.blockType };
}

//A selector that maps all connectors to react-flow Edges and exports it
export const selectConnectorsAsEdges = (state: GameState): Edge[] => {
    return Object.values(state.connectors).map((connector) => {
        return {
            id: connector.id,
            source: connector.inputBlockKey,
            target: connector.outputBlockKey,
        };
    });
}

//A selector that returns a map of each block type and the number of edges of that type for a given block
export const selectOutputBlockTypes = (state: GameState, blockID: string | undefined | null) => {
    const blockOutputEdgeCounts = new Map<string, number>();
    if (!blockID) return blockOutputEdgeCounts;
    Object.values(state.edges).forEach((edge) => {
        if (edge.source === blockID) {
            const targetNode = state.nodes.find((node) => node.id === edge.target);
            if (!targetNode) return;
            const edgeType = targetNode.data.blockType;
            const currentCount = blockOutputEdgeCounts.get(edgeType) || 0;
            blockOutputEdgeCounts.set(edgeType, currentCount + 1);
        }
    });
    return blockOutputEdgeCounts;
}

export const selectInitialRootIOPS = (state: GameState) => state.initialRootIOPS;

export const selectRootBlockIOPS = (state: GameState) => {
    const rootBlock = state.blockIOPS[ROOT_BLOCK_ID];
    return rootBlock ? rootBlock.outputIOPS : null;
}

export const selectTrafficPattern = (state: GameState) => state.trafficPattern;